import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Map from "./map.js";
import Button from "./button.js";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 600px;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    flex-direction: column;
  }
`;
const WrapperBig = styled(Wrapper)`
  flex-direction: column;
  margin: ${({ disableGutters }) => (disableGutters ? "0" : "0 40px")};
  > div {
    &:nth-child(2) {
      min-height: 600px;
    }
  }
`;
const MapWrapper = styled.div`
  width: 70%;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: 100%;
  }
`;
const TextBox = styled.div`
  display: flex;
  width: 30%;
  flex-direction: column;
  margin: 1rem 0 0 5rem;
  a {
    color: var(--color-body-link);
    text-decoration: none;
    margin: 0 10px;
    &:hover {
      text-decoration: underline;
    }
  }
  p,
  h3 {
    color: var(--color-body-text);
    font-weight: normal;
  }
  p {
    font-family: var(--font-paragraphs);
    font-size: 1rem;
    text-align: left;
  }
  h3 {
    font-family: var(--font-headings);
    margin-top: 15px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: 100%;
    margin: 1rem 0;
  }
`;
const TextBoxBigMap = styled(TextBox)`
  display: none;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    display: flex;
    width: 100%;
    margin-left: 0;
    padding-left: 20px;
  }
  p {
    text-align: left;
  }
  a {
    margin-left: 0;
  }
`;
const BoldText = styled.p`
  font-weight: 900;
  margin: 5px 10px !important;
  font-size: 18px !important;
`;
const BigBoldText = styled.p`
  font-weight: 900;
  margin: 5px 10px !important;
  font-size: 18px !important;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin: 5px 0 !important;
  }
`;
const PhoneNumber = styled.a`
  color: var(--color-body-link);
  font-family: var(--font-links);
  text-decoration: none;
  padding: 8px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin: 5px 10px;
  &:hover {
    text-decoration: underline;
  }
`;

export default function Location({
  btnUrl,
  children,
  variant,
  disableGutters,
  company,
  street,
  postal,
  email,
  phone,
  lat,
  lng,
  label,
}) {
  if (variant === "big") {
    return (
      <WrapperBig disableGutters={disableGutters}>
        <Map
          style={{ width: "100%", height: "100%" }}
          center={{ lat: lat, lng: lng }}
          zoom={16}
        />
        {company && street && postal && (
          <TextBoxBigMap>
            <BigBoldText>{company}</BigBoldText>
            <p>{street}</p>
            <p>{postal}</p>
            {phone && <PhoneNumber href={`tel:${phone}`}>{phone}</PhoneNumber>}
            {email && (
              <PhoneNumber
                href={`mailto:${email}`}
                style={{ marginBottom: "10px" }}
              >
                {email}
              </PhoneNumber>
            )}
          </TextBoxBigMap>
        )}
      </WrapperBig>
    );
  } else if (variant === "small") {
    return (
      <Wrapper>
        <MapWrapper>
          <Map
            style={{ height: "500px" }}
            center={{ lat: lat, lng: lng }}
            zoom={16}
          />
        </MapWrapper>
        {company && street && postal && (
          <TextBox>
            <BoldText dangerouslySetInnerHTML={{ __html: company }} />
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
              target="_blank"
              rel="noreferrer"
            >
              <p>{street}</p>
              <p>{postal}</p>
            </a>
            {phone && <PhoneNumber href={`tel:${phone}`}>{phone}</PhoneNumber>}
            {email && (
              <PhoneNumber
                href={`mailto:${email}`}
                style={{ marginBottom: "10px" }}
              >
                {email}
              </PhoneNumber>
            )}
            {children}
            <Button
              url={btnUrl}
              variant={"outlined"}
              label={label}
              style={{ margin: "0" }}
            />
          </TextBox>
        )}
      </Wrapper>
    );
  }
}

Location.propTypes = {
  btnUrl: PropTypes.string.isRequired,
  children: PropTypes.node,
  variant: PropTypes.oneOf(["big", "small"]),
  disableGutters: PropTypes.bool,
  company: PropTypes.string,
  street: PropTypes.string,
  postal: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  lat: PropTypes.number,
  lng: PropTypes.number,
  label: PropTypes.string,
};
Location.defaultProps = {
  children: undefined,
  variant: "big",
  disableGutters: false,
  company: undefined,
  street: undefined,
  postal: undefined,
  email: undefined,
  phone: undefined,
  lat: undefined,
  lng: undefined,
  label: undefined,
};
